import React, { useState } from 'react';
import axios from 'axios';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Calendar,
  Users,
  Baby,
  UserCheck,
  Loader2,
  Download,
  FileDown,
  Search,
  AlertTriangle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can access this report
const ALLOWED_ROLES = ['system_admin', 'executive', 'operations_manager'];

const CharlotteFoodPantryReportPage = () => {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Check if user has access
  const hasAccess = ALLOWED_ROLES.includes(user?.role);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/charlotte-food-pantry`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setReportData(response.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Unknown') return 'Unknown';
    try {
      return formatDateUtil(dateString) || dateString;
    } catch {
      return dateString;
    }
  };

  const exportReport = () => {
    if (!reportData) return;

    // Create CSV content
    let csv = 'Charlotte Food Pantry Monthly Report\n';
    csv += `Date Range: ${formatDate(reportData.date_range.start_date)} - ${formatDate(reportData.date_range.end_date)}\n`;
    csv += `Generated: ${formatDate(reportData.generated_at)}\n`;
    csv += `Generated By: ${reportData.generated_by}\n\n`;
    
    csv += 'Summary Metrics (Unduplicated Counts)\n';
    csv += `Number of People Served (Unduplicated),${reportData.metrics.number_of_people_served}\n`;
    csv += `Adults Ages 19-64 (Unduplicated),${reportData.metrics.adults_19_to_64}\n`;
    csv += `Adults Ages 65 and Older (Unduplicated),${reportData.metrics.adults_65_and_older}\n`;
    csv += `Number of Children (Unduplicated),${reportData.metrics.number_of_children}\n\n`;
    
    csv += 'Participant Service List\n';
    csv += 'Participant Name,Date of Service,Service Type\n';
    reportData.participant_service_list.forEach(item => {
      csv += `"${item.participant_name}",${item.date_of_service},"${item.service_type}"\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `charlotte-food-pantry-report-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const exportPdf = async () => {
    if (!reportData || !startDate || !endDate) return;
    
    setExportingPdf(true);
    try {
      const response = await axios.post(
        `${API}/reports/charlotte-food-pantry/export-pdf`,
        {
          start_date: startDate,
          end_date: endDate
        },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Charlotte_Food_Pantry_Monthly_Report_${startDate}_to_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF report');
    } finally {
      setExportingPdf(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="p-6">
        <Card className="card-shadow max-w-lg mx-auto">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-bold text-destructive mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                Only System Administrators, Executives, and Operations Managers can access this report.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/reports">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Charlotte Food Pantry Monthly Report</h1>
            <p className="text-muted-foreground">
              Generate reports for Food Pantry services in Charlotte, NC
            </p>
          </div>
        </div>
        {reportData && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportPdf}
              disabled={exportingPdf}
              data-testid="charlotte-export-pdf-btn"
            >
              {exportingPdf ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4 mr-2" />
              )}
              Export PDF
            </Button>
            <Button onClick={exportReport} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {/* Date Filters */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Report Date Range
          </CardTitle>
          <CardDescription>
            Select the date range for the report
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-48"
                data-testid="report-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-48"
                data-testid="report-end-date"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => {
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                setStartDate(firstDay.toISOString().split('T')[0]);
                setEndDate(lastDay.toISOString().split('T')[0]);
              }}
              data-testid="current-month-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Current Month
            </Button>
            <Button 
              onClick={generateReport} 
              disabled={loading || !startDate || !endDate}
              data-testid="generate-report-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-shadow bg-gradient-to-br from-green-50 to-white border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Number of People Served</p>
                    <p className="text-3xl font-bold text-green-700">
                      {reportData.metrics.number_of_people_served}
                    </p>
                    <p className="text-xs text-green-600">(Unduplicated)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Adults Ages 19-64</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {reportData.metrics.adults_19_to_64}
                    </p>
                    <p className="text-xs text-blue-600">(Unduplicated)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow bg-gradient-to-br from-purple-50 to-white border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Adults Ages 65+</p>
                    <p className="text-3xl font-bold text-purple-700">
                      {reportData.metrics.adults_65_and_older}
                    </p>
                    <p className="text-xs text-purple-600">(Unduplicated)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow bg-gradient-to-br from-orange-50 to-white border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Baby className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Number of Children</p>
                    <p className="text-3xl font-bold text-orange-700">
                      {reportData.metrics.number_of_children}
                    </p>
                    <p className="text-xs text-orange-600">(Unduplicated)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Participant Service List */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Participant Service List
              </CardTitle>
              <CardDescription>
                List of participants served during {formatDate(reportData.date_range.start_date)} - {formatDate(reportData.date_range.end_date)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData.participant_service_list.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="service-list-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">
                          Participant Name
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">
                          Date of Service
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.participant_service_list.map((item, index) => (
                        <tr 
                          key={`${item.participant_id}-${item.date_of_service}-${index}`}
                          className="border-b border-border last:border-0 hover:bg-muted/50"
                        >
                          <td className="py-3 px-4">
                            <span className="font-medium">{item.participant_name}</span>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {formatDate(item.date_of_service)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No qualifying services found in the selected date range</p>
                  <p className="text-sm mt-1">
                    Services are counted from case notes with subject &quot;New Applicant Intake &amp; Enrollment/Initial Box&quot; or &quot;Returning Box Distribution&quot;
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Export Buttons */}
          <div className="flex justify-center gap-4">
            <Button 
              variant="outline" 
              onClick={exportReport}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Report Footer */}
          <div className="text-center text-sm text-muted-foreground">
            <p>
              Report generated on {formatDate(reportData.generated_at)} by {reportData.generated_by}
            </p>
          </div>
        </>
      )}

      {/* Initial State */}
      {!reportData && !loading && (
        <Card className="card-shadow">
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">Select a Date Range</h3>
              <p>Choose start and end dates above to generate the report</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CharlotteFoodPantryReportPage;
